import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '../utils/api';
import { Building2, Filter, ChevronDown, ChevronUp, Trophy, Search } from 'lucide-react';
import { acqDenominator, invalidSeatCount } from '../utils/seat-rate.mjs';

const colors = {
  text: 'var(--ink)',
  textDim: 'var(--ink-soft)',
  textMute: 'var(--ink-mute)',
  border: 'var(--rule-strong)',
  primary: '#33406E',
  primaryLight: '#33406E',
  catch: '#33406E',
  sticker: '#33406E',
  talk: '#33406E',
  fulltalk: '#33406E',
  seat: '#33406E',
  acq: '#1E5540',
  success: '#1E5540',
  warning: '#5C4718',
  danger: 'var(--loss)',
};

// 商材別の色（ADR 0027）。
// ピルは任意の2商材が隣り合うので全ペア判定が要る。dataviz の検証済みパレット dark 列の
// 先頭3スロット（blue / orange / aqua）で、面色 #172134 に対して全チェック通過:
//   明度帯 OKLCH L 0.48–0.67 / 彩度下限 / CVD 全ペア最小 ΔE 9.4 / 通常視 20.9 / コントラスト 3:1 以上
// 色を付けられるのは3つまで（4つ目以降は全ペア判定を通らない）。
// 残りは中立色にして、商材名のラベルで識別させる（識別を色だけに頼らない）。
const PRODUCT_COLORS = ['#3987e5', '#d95926', '#199e70'];
const PRODUCT_NEUTRAL = 'var(--ink-mute)';

// 色は「商材そのもの」に紐づける。件数の多い順で割り当てると、期間を変えた拍子に
// 色が入れ替わって別の商材に見える。並び順はサーバが返す product_order（選択肢管理の順）。
function productColorOf(order) {
  const index = new Map((order || []).map((p, i) => [p, i]));
  return (product) => {
    const i = index.get(product);
    return (i != null && i < PRODUCT_COLORS.length) ? PRODUCT_COLORS[i] : PRODUCT_NEUTRAL;
  };
}

const card = {
  background: 'var(--field)',
  border: `1px solid ${colors.border}`,
  boxShadow: 'none',
  borderRadius: 16,
  padding: 18,
  marginBottom: 14,
};

const sectionLabel = {
  fontSize: 'var(--t-note)', fontWeight: 700, color: colors.textDim,
  marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6,
};

const inputStyle = {
  width: '100%',
  padding: '11px 12px',
  background: 'var(--field)',
  border: `1px solid ${colors.border}`,
  borderRadius: 10,
  color: colors.text,
  fontSize: 15,
  outline: 'none',
  colorScheme: 'dark',
  minHeight: 44,
  boxSizing: 'border-box',
};

const toYmd = (d) => d.toLocaleDateString('sv-SE');
const today = () => toYmd(new Date());

/* ====== KPIピル ====== */
function Kpi({ label, value, color }) {
  return (
    <div style={{
      flex: '1 1 80px',
      minWidth: 70,
      background: 'var(--paper-sunk)',
      border: `1px solid color-mix(in srgb, ${color} 13%, transparent)`,
      borderRadius: 10,
      padding: '8px 10px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 'var(--t-note)', color: colors.textMute, marginBottom: 2, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color }}>{value ?? 0}</div>
    </div>
  );
}

// 商材1つぶん。色は左の点だけが持ち、商材名と件数は文字色のトークンのまま
// （色を文字に塗ると、中立色の商材が「薄い＝重要でない」ように見える）。
function ProductPill({ label, value, color }) {
  return (
    <span style={{
      fontSize: 'var(--t-note)', padding: '4px 10px',
      background: 'var(--paper-sunk)',
      borderRadius: 8, fontWeight: 700, color: 'var(--ink-soft)',
      display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
    }}>
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {label} <span style={{ color: 'var(--ink)' }}>{value}</span>
    </span>
  );
}

// 商材別の内訳。0件は出さない（サーバ側で落としてある）。
function ProductPills({ products, colorOf }) {
  if (!products || products.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
      {products.map(p => (
        <ProductPill key={p.product} label={p.product} value={p.n} color={colorOf(p.product)} />
      ))}
    </div>
  );
}

/* ====== ユーザー行 ====== */
function UserRow({ u, colorOf }) {
  // ファネル: キャッチ→ティッシュ→トーク開始→フルトーク→着座→獲得（ADR-0002）
  // 獲得率の分母は有効着座（「有効じゃない」を除く。ADR 0028）
  const acqDen       = acqDenominator(u);
  const acqRate      = acqDen > 0 ? ((u.acq / acqDen) * 100).toFixed(0) : 0;
  const acqExcluded  = invalidSeatCount(u);
  const stickerRate  = u.catch > 0 ? ((u.sticker / u.catch) * 100).toFixed(0) : 0;
  const talkRate     = u.sticker > 0 ? ((u.talk / u.sticker) * 100).toFixed(0) : 0;
  const fulltalkRate = u.talk > 0 ? ((u.fulltalk / u.talk) * 100).toFixed(0) : 0;
  const seatRate     = u.fulltalk > 0 ? ((u.seat / u.fulltalk) * 100).toFixed(0) : 0;
  return (
    <div style={{
      padding: '12px',
      background: 'var(--paper-sunk)',
      borderRadius: 10,
      marginBottom: 8,
      border: `1px solid ${colors.border}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: colors.text }}>{u.user_name || `User#${u.user_id}`}</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: colors.acq }}>{u.acq || 0}</span>
          <span style={{ fontSize: 'var(--t-note)', color: colors.textMute }}>獲得</span>
          <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: colors.acq, marginLeft: 4 }}>({acqRate}%)</span>
        </div>
      </div>

      {/* 商材別（ADR 0027。商材が増えても自動で出る） */}
      <ProductPills products={u.products} colorOf={colorOf} />

      {/* 全KPI */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Kpi label="キャッチ"   value={u.catch} color={colors.catch} />
        <Kpi label="ティッシュ" value={u.sticker} color={colors.sticker} />
        <Kpi label="トーク"     value={u.talk} color={colors.talk} />
        <Kpi label="フルトーク" value={u.fulltalk} color={colors.fulltalk} />
        <Kpi label="着座"       value={u.seat} color={colors.seat} />
        <Kpi label="獲得"       value={u.acq} color={colors.acq} />
      </div>

      {/* 転換率 */}
      <div style={{ display: 'flex', gap: 8, fontSize: 'var(--t-note)', color: colors.textMute, marginTop: 6, flexWrap: 'wrap' }}>
        <span>ティッシュ率 <strong style={{ color: colors.sticker }}>{stickerRate}%</strong></span>
        <span>トーク率 <strong style={{ color: colors.talk }}>{talkRate}%</strong></span>
        <span>フルトーク率 <strong style={{ color: colors.fulltalk }}>{fulltalkRate}%</strong></span>
        <span>着座率 <strong style={{ color: colors.seat }}>{seatRate}%</strong></span>
        <span>獲得率 <strong style={{ color: colors.acq }}>{acqRate}%</strong>
          {acqExcluded > 0 && <span title={`「有効じゃない」${acqExcluded}件を分母から除いています`}>（有効着座 {acqDen}）</span>}
        </span>
      </div>
    </div>
  );
}

/* ====== ブースカード ====== */
function BoothCard({ booth, rank, colorOf }) {
  const [open, setOpen] = useState(false);
  // ファネル: キャッチ→ティッシュ→トーク開始→フルトーク→着座→獲得（ADR-0002）
  const stickerRate  = booth.catch > 0 ? ((booth.sticker / booth.catch) * 100).toFixed(0) : 0;
  const talkRate     = booth.sticker > 0 ? ((booth.talk / booth.sticker) * 100).toFixed(0) : 0;
  const fulltalkRate = booth.talk > 0 ? ((booth.fulltalk / booth.talk) * 100).toFixed(0) : 0;
  const seatRate     = booth.fulltalk > 0 ? ((booth.seat / booth.fulltalk) * 100).toFixed(0) : 0;
  // 獲得率の分母は有効着座（「有効じゃない」を除く。ADR 0028）
  const acqDen       = acqDenominator(booth);
  const acqRate      = acqDen > 0 ? ((booth.acq / acqDen) * 100).toFixed(1) : 0;
  const acqExcluded  = invalidSeatCount(booth);
  const catchAcqRate = booth.catch > 0 ? ((booth.acq / booth.catch) * 100).toFixed(1) : 0;
  const hasUsers = (booth.users || []).length > 0;
  const rankColor = rank === 1 ? 'var(--hold)' : rank === 2 ? 'var(--ink)' : rank === 3 ? 'var(--hold)' : colors.textMute;

  return (
    <div style={{
      background: 'var(--paper-sunk)',
      border: `1px solid ${colors.border}`,
      borderRadius: 14,
      marginBottom: 12,
      overflow: 'hidden',
    }}>
      {/* ヘッダー */}
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            {rank <= 3 && (
              <span style={{
                fontSize: 'var(--t-note)', fontWeight: 800,
                padding: '2px 8px',
                background: `color-mix(in srgb, ${rankColor} 13%, transparent)`,
                color: rankColor,
                borderRadius: 6,
                flexShrink: 0,
              }}>#{rank}</span>
            )}
            <Building2 size={16} color={colors.primaryLight} style={{ flexShrink: 0 }} />
            <span style={{
              fontSize: 16, fontWeight: 800, color: colors.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{booth.booth_name || `Booth#${booth.booth_id}`}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexShrink: 0 }}>
            <span style={{ fontSize: 28, fontWeight: 800, color: colors.acq }}>{booth.acq || 0}</span>
            <span style={{ fontSize: 'var(--t-note)', color: colors.textMute }}>獲得</span>
          </div>
        </div>

        {/* 商材別（ADR 0027） */}
        <ProductPills products={booth.products} colorOf={colorOf} />

        {/* 全KPI */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          <Kpi label="キャッチ"   value={booth.catch} color={colors.catch} />
          <Kpi label="ティッシュ" value={booth.sticker} color={colors.sticker} />
          <Kpi label="トーク"     value={booth.talk} color={colors.talk} />
          <Kpi label="フルトーク" value={booth.fulltalk} color={colors.fulltalk} />
          <Kpi label="着座"       value={booth.seat} color={colors.seat} />
          <Kpi label="獲得"       value={booth.acq} color={colors.acq} />
        </div>

        {/* 内訳 */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={{ fontSize: 'var(--t-note)', padding: '3px 8px', background: 'var(--loss-bg)', color: colors.danger, borderRadius: 6, fontWeight: 700 }}>失注 {booth.lost || 0}</span>
          <span style={{ fontSize: 'var(--t-note)', padding: '3px 8px', background: 'var(--mark-soft)', color: colors.primaryLight, borderRadius: 6, fontWeight: 700 }}>後追 {booth.followup || 0}</span>
        </div>

        {/* 転換率 */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6,
          padding: '8px',
          background: 'rgba(0,0,0,0.2)', borderRadius: 8,
        }}>
          {[
            ['ティッシュ率', stickerRate, colors.sticker],
            ['トーク率', talkRate, colors.talk],
            ['フルトーク率', fulltalkRate, colors.fulltalk],
            ['着座率', seatRate, colors.seat],
            // 分母から「有効じゃない」を除いたときだけ、有効着座の件数を下に添える（ADR 0028）
            ['獲得率', acqRate, colors.acq, acqExcluded > 0 ? `有効着座 ${acqDen}` : null],
            ['キャッチ→獲得', catchAcqRate, colors.success],
          ].map(([lbl, val, col, sub]) => (
            <div key={lbl} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--t-note)', color: colors.textMute, marginBottom: 2 }}>{lbl}</div>
              <div style={{ fontSize: 'var(--t-note)', fontWeight: 800, color: col }}>{val}%</div>
              {sub && <div style={{ fontSize: 'var(--t-note)', color: colors.textMute, marginTop: 2 }}>{sub}</div>}
            </div>
          ))}
        </div>

        {/* 展開 */}
        {hasUsers && (
          <button
            onClick={() => setOpen(o => !o)}
            style={{
              marginTop: 12, width: '100%',
              padding: '10px',
              background: 'var(--mark-soft)',
              border: `1px solid ${colors.border}`,
              borderRadius: 10,
              color: colors.primaryLight,
              fontSize: 'var(--t-note)', fontWeight: 700,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              minHeight: 44,
            }}
          >
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            担当者別を見る（{booth.users.length}名）
          </button>
        )}
      </div>

      {/* 担当者別 */}
      {open && hasUsers && (
        <div style={{ padding: '0 16px 16px 16px', borderTop: `1px solid ${colors.border}` }}>
          <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: colors.textMute, margin: '12px 0 8px' }}>
             このブースで稼働した担当者
          </div>
          {booth.users.map(u => <UserRow key={u.user_id} u={u} colorOf={colorOf} />)}
        </div>
      )}
    </div>
  );
}

/* ====== メイン ====== */
export default function BoothAnalytics({ user }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const [preset, setPreset] = useState('month');
  const [dateFrom, setDateFrom] = useState(toYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [dateTo, setDateTo] = useState(toYmd(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)));
  const [selectedBoothIds, setSelectedBoothIds] = useState(new Set()); // 複数選択。空ならall
  const [search, setSearch] = useState('');

  const applyPreset = (p) => {
    setPreset(p);
    const now = new Date();
    if (p === 'today') {
      setDateFrom(today()); setDateTo(today());
    } else if (p === 'week') {
      setDateFrom(toYmd(new Date(now - 6 * 86400000))); setDateTo(today());
    } else if (p === 'month') {
      setDateFrom(toYmd(new Date(now.getFullYear(), now.getMonth(), 1)));
      setDateTo(toYmd(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
    } else if (p === 'all') {
      setDateFrom('2020-01-01'); setDateTo(today());
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // ブース集計はこの画面だけが使うので、明示して頼む（ADR 0029。既定では返らない）
      const params = { date_from: dateFrom, date_to: dateTo, include: 'booth' };
      const d = await api.getAnalyticsDashboard(params).catch(() => null);
      setDashboard(d);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // 日付またぎ対策：開いたまま日付が変わったら相対プリセットを新しい今日で再計算。
  const dayRef = useRef(today());
  useEffect(() => {
    const check = () => {
      const t = today();
      if (t === dayRef.current) return;
      dayRef.current = t;
      if (preset !== 'custom') applyPreset(preset);
    };
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    const id = setInterval(check, 30000);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', check);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', check); };
  }, [preset]);

  const allBooths = dashboard?.byBooth || [];

  // 検索＆ブースフィルタ適用
  // 商材の固定順と、それに紐づく色（ADR 0027）。
  // 期間やブースの絞り込みを変えても、同じ商材は同じ色のまま。
  const productOrder = useMemo(() => dashboard?.product_order || [], [dashboard]);
  const colorOf = useMemo(() => productColorOf(productOrder), [productOrder]);

  const filteredBooths = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = allBooths;
    if (q) list = list.filter(b => (b.booth_name || '').toLowerCase().includes(q));
    if (selectedBoothIds.size > 0) list = list.filter(b => selectedBoothIds.has(b.booth_id));
    return list;
  }, [allBooths, search, selectedBoothIds]);

  // 合計サマリー
  const totals = useMemo(() => {
    return filteredBooths.reduce((acc, b) => ({
      catch: acc.catch + (b.catch || 0),
      sticker: acc.sticker + (b.sticker || 0),
      talk: acc.talk + (b.talk || 0),
      fulltalk: acc.fulltalk + (b.fulltalk || 0),
      seat: acc.seat + (b.seat || 0),
      acq: acc.acq + (b.acq || 0),
      lost: acc.lost + (b.lost || 0),
      followup: acc.followup + (b.followup || 0),
    }), { catch: 0, sticker: 0, talk: 0, fulltalk: 0, seat: 0, acq: 0, lost: 0, followup: 0 });
  }, [filteredBooths]);

  // 合計の商材別。表示中のブースの内訳を足し合わせる（サーバの並び順を保つ）。
  const totalProducts = useMemo(() => {
    const acc = new Map();
    filteredBooths.forEach(b => (b.products || []).forEach(p => {
      acc.set(p.product, (acc.get(p.product) || 0) + (p.n || 0));
    }));
    const rank = new Map((productOrder || []).map((x, i) => [x, i]));
    const rankOf = (x) => (rank.has(x) ? rank.get(x) : Number.MAX_SAFE_INTEGER);
    return [...acc.entries()].map(([product, n]) => ({ product, n }))
      .sort((a, b) => (rankOf(a.product) - rankOf(b.product)) || (b.n - a.n));
  }, [filteredBooths, productOrder]);

  const toggleBooth = (id) => {
    setSelectedBoothIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearBoothFilter = () => setSelectedBoothIds(new Set());

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 'var(--t-note)', color: colors.textMute, marginBottom: 4 }}>{user.name} さん</div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: colors.text }}>
           ブース分析
        </h1>
      </div>

      {/* フィルター */}
      <div style={card}>
        <div style={sectionLabel}><Filter size={14} /> フィルター</div>

        {/* 期間プリセット */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {[
            ['today', '今日'],
            ['week', '直近7日'],
            ['month', '今月'],
            ['all', '全期間'],
            ['custom', 'カスタム'],
          ].map(([k, label]) => (
            <button key={k} onClick={() => applyPreset(k)} style={{
              flex: '1 1 0', minWidth: 60,
              padding: '10px 8px',
              borderRadius: 10,
              fontSize: 'var(--t-note)', fontWeight: 700,
              border: `1.5px solid ${preset === k ? colors.primary : colors.border}`,
              background: preset === k ? `color-mix(in srgb, ${colors.primary} 13%, transparent)` : 'transparent',
              color: preset === k ? colors.primaryLight : colors.textDim,
              cursor: 'pointer',
              minHeight: 44,
            }}>{label}</button>
          ))}
        </div>

        {/* 期間 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: colors.textMute, marginBottom: 4, display: 'block' }}>開始日</label>
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPreset('custom'); }} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: colors.textMute, marginBottom: 4, display: 'block' }}>終了日</label>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPreset('custom'); }} style={inputStyle} />
          </div>
        </div>

        {/* ブース名検索 */}
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: colors.textMute, marginBottom: 4, display: 'block' }}>
             ブース名で絞る
          </label>
          <div style={{ position: 'relative' }}>
            <Search size={14} color={colors.textMute} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              type="text" inputMode="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="ブース名を入力..."
              style={{ ...inputStyle, paddingLeft: 36 }}
            />
          </div>
        </div>

        {/* ブースチップ複数選択 */}
        {allBooths.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: colors.textMute }}>
                絞り込み（複数選択可）
              </label>
              {selectedBoothIds.size > 0 && (
                <button onClick={clearBoothFilter} style={{
                  background: 'transparent', border: 'none', color: colors.danger, fontSize: 'var(--t-note)', cursor: 'pointer', fontWeight: 700,
                }}>クリア ({selectedBoothIds.size})</button>
              )}
            </div>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6,
              maxHeight: 160, overflowY: 'auto',
              padding: 4,
              background: 'rgba(0,0,0,0.2)',
              borderRadius: 10,
            }}>
              {allBooths.map(b => {
                const active = selectedBoothIds.has(b.booth_id);
                return (
                  <button
                    key={b.booth_id}
                    onClick={() => toggleBooth(b.booth_id)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 10,
                      fontSize: 'var(--t-note)', fontWeight: 600,
                      border: `1.5px solid ${active ? colors.primary : 'var(--rule-strong)'}`,
                      background: active ? `color-mix(in srgb, ${colors.primary} 13%, transparent)` : 'var(--paper-sunk)',
                      color: active ? colors.primaryLight : colors.textDim,
                      cursor: 'pointer',
                      minHeight: 40,
                    }}
                  >
                    {b.booth_name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.textMute }}>読み込み中...</div>
      ) : (
        <>
          {/* 合計サマリー */}
          {filteredBooths.length > 0 && (
            <div style={card}>
              <div style={sectionLabel}><Trophy size={14} color={colors.acq} /> 合計KPI（{filteredBooths.length}ブース）</div>

              {/* 商材別（ADR 0027。商材が増えても自動で出る） */}
              {totalProducts.length > 0 && (
                <div style={{
                  display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap',
                  padding: '14px',
                  background: 'var(--paper-sunk)',
                  borderRadius: 12,
                  border: `1px solid ${colors.border}`,
                }}>
                  {totalProducts.map(p => (
                    <div key={p.product} style={{ flex: '1 1 90px', minWidth: 90 }}>
                      <div style={{ fontSize: 'var(--t-note)', color: colors.textMute, marginBottom: 4, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: colorOf(p.product), flexShrink: 0 }} />
                        {p.product}
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--ink)' }}>{p.n}</div>
                    </div>
                  ))}
                  <div style={{ flex: '1 1 90px', minWidth: 90 }}>
                    <div style={{ fontSize: 'var(--t-note)', color: colors.textMute, marginBottom: 4, fontWeight: 600 }}>合計獲得</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: colors.acq }}>{totals.acq}</div>
                  </div>
                </div>
              )}

              {/* 全KPI */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                <Kpi label="キャッチ"   value={totals.catch}    color={colors.catch} />
                <Kpi label="ティッシュ" value={totals.sticker}  color={colors.sticker} />
                <Kpi label="トーク"     value={totals.talk}     color={colors.talk} />
                <Kpi label="フルトーク" value={totals.fulltalk} color={colors.fulltalk} />
                <Kpi label="着座"       value={totals.seat}     color={colors.seat} />
                <Kpi label="獲得"       value={totals.acq}      color={colors.acq} />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 'var(--t-note)', padding: '4px 10px', background: 'var(--loss-bg)', color: colors.danger, borderRadius: 8, fontWeight: 700 }}>失注 {totals.lost}</span>
                <span style={{ fontSize: 'var(--t-note)', padding: '4px 10px', background: 'var(--mark-soft)', color: colors.primaryLight, borderRadius: 8, fontWeight: 700 }}>後追 {totals.followup}</span>
              </div>
            </div>
          )}

          {/* ブース別カード */}
          {filteredBooths.length === 0 ? (
            <div style={{ ...card, textAlign: 'center', color: colors.textMute, padding: 40 }}>
              <Building2 size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
              <div style={{ fontSize: 'var(--t-note)' }}>該当するブースのデータがありません</div>
              <div style={{ fontSize: 'var(--t-note)', marginTop: 4 }}>期間を広げるか、ブース選択をクリアしてください</div>
            </div>
          ) : (
            filteredBooths.map((b, i) => <BoothCard key={b.booth_id} booth={b} rank={i + 1} colorOf={colorOf} />)
          )}
        </>
      )}
    </div>
  );
}
